import React from 'react';

const services = [
  {
    tag: 'SERVICE_01',
    code: 'DEC_SUPPORT',
    title: 'Clarity & Decision Support',
    valueProp: 'Know what to build and why, before anyone writes code.',
    desc:
      'The work that stops projects failing: turning fuzzy business needs into clearly scoped, measurable plans, and turning raw data into reporting that executives actually use. This is business analysis and analytics working together.',
    bullets: [
      'Requirements gathering, user stories, and functional specifications',
      'As-is and to-be process mapping',
      'Stakeholder workshops, discovery sessions, and facilitation',
      'Options analysis and clearly scoped delivery planning',
      'Power BI and Excel dashboards, with data storytelling for executive audiences',
      'SQL and Python analysis turning business questions into measurable answers',
      'Root-cause analysis and process improvement',
      'UAT support and change management',
    ],
    engagedAs: 'Business Analyst, Data Analyst',
  },
  {
    tag: 'SERVICE_02',
    code: 'DATA_ENG',
    title: 'Data Foundations & Engineering',
    valueProp: 'One reliable source of truth, instead of spreadsheets that disagree.',
    desc:
      'The plumbing that everything else depends on. We design and build the pipelines and central stores that pull data out of disconnected systems and make it trustworthy, with monitoring so you know when something breaks before your stakeholders do.',
    bullets: [
      'ETL pipeline design and build across multiple source systems',
      'Central data stores for analysis, linkage, and reporting',
      'Data cleaning, validation, and quality checks',
      'Integration specifications: interfaces, error handling, and monitoring',
      'Observability and reporting dashboards for data integrity and system health',
      'PostgreSQL and MSSQL, Azure Data Factory, Data Lake, and Databricks',
      'Secure data handling and practical data governance',
      'GIS and field data capture, including offline collection with cloud sync',
    ],
    engagedAs: 'Data Engineer, Data Analyst',
  },
  {
    tag: 'SERVICE_03',
    code: 'ML_SYSTEMS',
    title: 'Applied AI & Machine Learning',
    valueProp: 'AI that runs in production and earns its place, not a demo that never ships.',
    desc:
      'End-to-end machine learning and AI, from framing the problem through modelling, evaluation, deployment, and monitoring. Classical models where they fit, retrieval-augmented generation and computer vision where they fit, and the MLOps to keep it all running.',
    bullets: [
      'Supervised ML for classification and regression, with honest evaluation',
      'Retrieval-augmented generation (RAG) using hybrid vector and keyword search',
      'Document interpretation, matching, and generation workflows',
      'Object detection and computer vision (YOLOv7) for recognition and analysis',
      'Real-time ML pipelines and feature stores',
      'Model monitoring and observability in production',
      'Containerisation with Docker and CI/CD for repeatable, secure deployment',
      'Azure ML and AWS, with advice on safe and sustainable AI adoption',
    ],
    engagedAs: 'Data Scientist, ML Engineer, MLOps Engineer',
  },
];

const Services = () => (
  <section
    id="services"
    style={{ background: '#070a0e', borderTop: '1px solid rgba(255,255,255,0.05)' }}
  >
    <div
      className="zk-section-pad zk-section-gutter"
      style={{ maxWidth: '1180px', margin: '0 auto', padding: '120px 56px' }}
    >
      <div style={{ marginBottom: '54px' }}>
        <span style={{ fontFamily: 'var(--zk-mono)', fontSize: '12px', letterSpacing: '0.28em', color: '#ff3ea5' }}>
          {'// SERVICES'}
        </span>
        <h2
          style={{
            margin: '18px 0 0',
            fontSize: 'clamp(34px, 4vw, 52px)',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1.04,
          }}
        >
          Three pillars, one practitioner.
        </h2>
      </div>

      <div
        className="zk-grid-services"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px',
          alignItems: 'stretch',
        }}
      >
        {services.map((s) => (
          <div key={s.tag} className="zk-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '22px' }}>
              <span
                style={{
                  fontFamily: 'var(--zk-mono)',
                  fontSize: '11px',
                  letterSpacing: '0.12em',
                  color: '#ff3ea5',
                  padding: '5px 10px',
                  border: '1px solid rgba(255,62,165,0.32)',
                  borderRadius: '2px',
                }}
              >
                [ {s.tag} ]
              </span>
              <span
                style={{
                  fontFamily: 'var(--zk-mono)',
                  fontSize: '11px',
                  letterSpacing: '0.1em',
                  color: 'rgba(232,238,240,0.4)',
                }}
              >
                {s.code}
              </span>
            </div>
            <h3 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 600, lineHeight: 1.18 }}>{s.title}</h3>
            <p
              style={{
                margin: '0 0 18px',
                fontSize: '14.5px',
                lineHeight: 1.5,
                color: '#38e0ff',
                fontWeight: 500,
                textWrap: 'pretty',
              }}
            >
              {s.valueProp}
            </p>
            <p
              style={{
                margin: '0 0 22px',
                fontSize: '13.5px',
                lineHeight: 1.62,
                color: 'rgba(232,238,240,0.66)',
                textWrap: 'pretty',
              }}
            >
              {s.desc}
            </p>
            <ul
              style={{
                listStyle: 'none',
                margin: '0 0 24px',
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '9px',
                flex: 1,
              }}
            >
              {s.bullets.map((b, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: '12.5px',
                    lineHeight: 1.45,
                    color: 'rgba(232,238,240,0.6)',
                    paddingLeft: '16px',
                    position: 'relative',
                  }}
                >
                  <span style={{ position: 'absolute', left: 0, color: '#ff3ea5' }}>›</span>
                  {b}
                </li>
              ))}
            </ul>
            <div
              style={{
                paddingTop: '18px',
                borderTop: '1px solid rgba(255,255,255,0.07)',
                fontFamily: 'var(--zk-mono)',
                fontSize: '10.5px',
                letterSpacing: '0.06em',
                color: 'rgba(232,238,240,0.42)',
              }}
            >
              ENGAGED_AS: {s.engagedAs}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
